import {
  DeleteOutlined,
  EditNote,
  Loop,
  NotificationsOutlined,
  PeopleAltOutlined,
  StopCircleOutlined,
} from "@mui/icons-material";
import { Checkbox } from "@mui/material";
import { AxiosError } from "axios";
import { EMAILID_COOKIE, MOCK_USER_COOKIE } from "constant";
import Cookies from "js-cookie";
import moment from "moment";
import { useAxios } from "myAxios";
import { ChangeEvent, FC, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { TodoItemObject } from "types/todolist";
import { PostParams } from "./EditTask";
import classes from "./TodoItem.module.scss";

const ViewTask: FC<{
  item: TodoItemObject;
  getOpenStatus: (openStatus: boolean) => void;
  tab?: string;
  mutate: any;
  mutateOverdue?: any;
}> = ({ item, getOpenStatus, tab = "today", mutate, mutateOverdue }) => {
  const [updating, setUpdating] = useState<boolean>(false);
  const axios = useAxios();
  //   const [open, setOpen] = useState(false);
  const [checked, setChecked] = useState(item.completed ?? false);
  //for only sub task or non-repeat task
  const isOverdue = useMemo(
    () =>
      moment()
        .tz(item.userTimezone)
        .isAfter(moment.tz(item.dueDate, item.userTimezone).add(1, "days")) && !!item.itemId,
    [item]
  );
  // for only main task
  const isNextDue = useMemo(
    () =>
      moment()
        .tz(item.userTimezone)
        .isAfter(moment.tz(item.nextDueDate, item.userTimezone).add(1, "days")) &&
      !item.itemId &&
      !!item.repeatMethod,
    [item]
  );
  const isEnded = !!item.repeatMethod && item.nextDueDate == null;
  const beyondEndDate = item.endDate
    ? useMemo(
        () => moment().tz(item.userTimezone).isAfter(moment.tz(item.endDate, item.userTimezone)),
        [item]
      )
    : false;

  // Get current user's email
  const userEmail = Cookies.get(EMAILID_COOKIE);
  const mockUserEmail = Cookies.get(MOCK_USER_COOKIE);
  const currentUserCecId = userEmail?.split("@")?.[0] ?? mockUserEmail?.split("@")?.[0];

  // Check if assignee is different from current user
  const shouldShowAssignee =
    item.assigneeName && item.assignedTo && item.assignedTo !== currentUserCecId;

  useEffect(() => {
    setChecked(item.completed ?? false);
  }, [mutate, item]);

  const handleChange = async (event: ChangeEvent<HTMLInputElement>) => {
    setChecked(event.target.checked);
    // todo: call api update current item status, then mutate list
    const postData: PostParams = {
      userTimezone: item.userTimezone,
      content: item.content,
      assignedTo: item.assignedTo,
      dueDate: item.dueDate ? moment(item.dueDate)?.format("YYYY-MM-DD") : null,
      repeatMethod: item.repeatMethod || "",
      endDate: item.endDate ? moment(item.endDate)?.format("YYYY-MM-DD") : null,
      completed: event.target.checked,
      itemId: item.itemId,
      rrule: item.rrule || "",
    };
    try {
      if (item?.id) {
        await axios.put(`/api/dataservice/v1/todos/${item?.id}`, postData);
        await mutate?.();
        await mutateOverdue?.();
        toast.success("Update task successfully!", {
          position: "top-center",
          autoClose: 3000,
        });
      }
    } catch (e) {
      toast.error((e as AxiosError)?.response?.data?.message || "Fail to update task", {
        position: "top-center",
        autoClose: 3000,
      });
    }
  };

  const updateItem = () => {
    getOpenStatus?.(true);
  };

  const deleteItem = async () => {
    if (item.id) {
      setUpdating(true);
      try {
        await axios.delete(
          item?.itemId
            ? `/api/dataservice/v1/todos/${item.id}?itemId=${item.itemId}`
            : `/api/dataservice/v1/todos/${item.id}`
        );
        await mutate?.();
        await mutateOverdue?.();
        toast.success("Deleted task successfully!", {
          position: "top-center",
          autoClose: 3000,
        });
      } catch (e) {
        toast.error((e as AxiosError)?.response?.data?.message || "Failed to delete task", {
          position: "top-center",
          autoClose: 3000,
        });
      } finally {
        setUpdating(false);
      }
    }
  };

  return (
    <div
      className={`${tab === "mytask" ? (item.itemId != null && item.showInMyself === false ? classes.subContent : classes.content) : classes.content} ${item.itemId != null && item.showInMyself === false && tab === "mytask" ? (isOverdue ? classes.overdueSubTask : classes.normalSubTask) : ""} `}
      style={{ position: "relative" }}
    >
      {updating && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundColor: "rgba(255,255,255,0.6)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 10,
          }}
        ></div>
      )}
      <div className={classes.wrapper}>
        <Checkbox
          value={item.id}
          checked={checked}
          onChange={handleChange}
          disabled={!!item.repeatMethod && isEnded && !item.itemId}
          sx={{
            "&:hover": {
              backgroundColor: "transparent",
            },
          }}
        />
        <div className={`${item.dueDate ? classes.main : classes.onlyTitle}`}>
          <div className={classes.titleWrapper}>
            <p className={`${classes.title} ${item.completed ? classes.completedTitle : ""}`}>
              {item.content}
            </p>
            {item.repeatMethod ? <Loop style={{ color: "#2C7FFF", marginLeft: 8 }} /> : null}
            {item.reminders && item.reminders.length > 0 ? (
              <NotificationsOutlined style={{ color: "#FFB800", marginLeft: 8 }} />
            ) : null}
            {item.repeatMethod && !item.completed && !item.itemId && isEnded && beyondEndDate ? (
              <div
                style={{
                  border: "1px solid #bdbdbd",
                  margin: "0 8px",
                  padding: "4px 8px",
                  borderRadius: 8,
                  display: "flex",
                  alignItems: "center",
                  fontSize: 12,
                  color: "#252525",
                  backgroundColor: "#f5f5f5",
                }}
              >
                <StopCircleOutlined
                  style={{ marginRight: 4 }}
                  fontSize="small"
                />
                {`Ended on ${moment.tz(item.endDate, item.userTimezone).format("ll")}`}
              </div>
            ) : null}
            {item.assigneeName && shouldShowAssignee && (
              <div className={classes.assigneeWrapper}>
                <PeopleAltOutlined />
                <p className={classes.assignee}>{item.assigneeName}</p>
              </div>
            )}
          </div>
          {item.dueDate ? (
            <div
              className={`${classes.dueInfo} ${
                (isOverdue || isNextDue) && !item.completed ? classes.overdue : ""
              }`}
            >
              {/* if today > duedate, then show overdue, then due date line all color red */}
              {!item.itemId && item?.repeatMethod ? "Next Due" : "Due"}:{" "}
              {!item.itemId && item?.repeatMethod
                ? item.nextDueDate == null
                  ? "N/A"
                  : moment(item.nextDueDate).format("ll")
                : moment(item.dueDate).format("ll")}{" "}
              {item.userTimezone && `(${item.userTimezone})`}
              {(isOverdue || isNextDue) && !item.completed && <p> &nbsp;(Overdue)</p>}
            </div>
          ) : null}
        </div>
        <div className={classes.right}>
          <EditNote
            style={{ marginRight: 30, cursor: "pointer" }}
            onClick={updateItem}
          />
          <DeleteOutlined
            style={{ cursor: "pointer" }}
            onClick={deleteItem}
          />
        </div>
      </div>
    </div>
  );
};
export default ViewTask;
